import React, { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { learningPathsApi, orgApi } from '../../api/client';

function formatDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function statusColor(status?: string | null) {
  if (status === 'completed') return 'success' as const;
  if (status === 'in_progress') return 'warning' as const;
  return 'default' as const;
}

const AssignLearningPathPage: React.FC = () => {
  const [paths, setPaths] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedPathId, setSelectedPathId] = useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [editGroup, setEditGroup] = useState<any | null>(null);
  const [editEmployeeIds, setEditEmployeeIds] = useState<string[]>([]);
  const [editDueDate, setEditDueDate] = useState('');

  const load = async () => {
    try {
      setError(null);
      const [pathRows, employeeRows, assignmentRows] = await Promise.all([
        learningPathsApi.list(),
        orgApi.listEmployees(),
        learningPathsApi.listAssignments(),
      ]);
      setPaths(pathRows);
      setEmployees(employeeRows);
      setAssignments(assignmentRows);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, any>();
    assignments.forEach((assignment) => {
      const path = paths.find((item) => item.id === assignment.learningPathId) || assignment.learningPath;
      if (!path?.id) return;
      if (!map.has(path.id)) map.set(path.id, { path, assignments: [] as any[] });
      map.get(path.id).assignments.push(assignment);
    });
    return Array.from(map.values()).map((group) => {
      const dates = group.assignments
        .map((item: any) => item.assignedDate || item.createdAt)
        .filter(Boolean)
        .sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime());
      return { ...group, latestAssignedDate: dates[0] || null };
    });
  }, [assignments, paths]);

  const activePaths = paths.filter((path) => !path.isArchived);
  const selectedPath = activePaths.find((path) => path.id === selectedPathId);

  const assignEmployees = async (pathId: string, employeeIds: string[], due?: string) => {
    setSubmitting(true);
    setError(null);
    try {
      for (const employeeId of employeeIds) {
        await learningPathsApi.assign(pathId, { employeeId, dueDate: due || undefined });
      }
      setSelectedPathId('');
      setSelectedEmployeeIds([]);
      setDueDate('');
      setEditGroup(null);
      setEditEmployeeIds([]);
      setEditDueDate('');
      setTab(0);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const removeAssignment = async (assignment: any) => {
    if (assignment.status && assignment.status !== 'not_started') return;
    const name = assignment.employee?.name || 'this employee';
    if (!window.confirm(`Remove ${name} from this learning path?`)) return;
    try {
      setSubmitting(true);
      await learningPathsApi.removeAssignment(assignment.id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const availableForEdit = employees.filter(
    (employee) => !editGroup?.assignments.some((item: any) => item.employeeId === employee.id),
  );

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>Assign Learning Path</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={<Badge badgeContent={groups.length} color="primary"><Box sx={{ pr: 2 }}>Assignments</Box></Badge>} />
        <Tab icon={<AddIcon />} iconPosition="start" label="New Assignment" />
      </Tabs>

      {tab === 0 && (
        groups.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" gutterBottom>No learning paths have been assigned yet.</Typography>
            <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={() => setTab(1)}>Create New Assignment</Button>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Learning Path</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}><GroupIcon fontSize="small" /> Employees</Box>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}><CalendarTodayIcon fontSize="small" /> Last Assigned</Box>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => {
                  const open = expandedId === group.path.id;
                  const sequential = group.path.isSequential !== false;
                  return (
                    <React.Fragment key={group.path.id}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedId(open ? null : group.path.id)}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                            <Box>
                              <Typography fontWeight={600}>{group.path.title}</Typography>
                              <Chip label={`v${group.path.version || 1}`} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip icon={<GroupIcon />} label={group.assignments.length} color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">{formatDate(group.latestAssignedDate)}</TableCell>
                        <TableCell align="center">
                          <Chip label={sequential ? 'Sequential' : 'Flexible'} size="small" color={sequential ? 'info' : 'default'} variant={sequential ? 'filled' : 'outlined'} />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Add more employees">
                            <IconButton color="primary" onClick={(event) => {
                              event.stopPropagation();
                              setEditGroup(group);
                              setEditEmployeeIds([]);
                              setEditDueDate('');
                            }}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 4 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Assigned Employees ({group.assignments.length})
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    {['Name', 'Email', 'Status', 'Assigned Date', 'Due Date'].map((column) => <TableCell key={column}>{column}</TableCell>)}
                                    <TableCell align="center">Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {group.assignments.map((assignment: any) => {
                                    const removable = !assignment.status || assignment.status === 'not_started';
                                    return (
                                      <TableRow key={assignment.id}>
                                        <TableCell>{assignment.employee?.name || 'Unknown'}</TableCell>
                                        <TableCell>{assignment.employee?.email || '—'}</TableCell>
                                        <TableCell>
                                          <Chip
                                            label={(assignment.status || 'not_started').replace('_', ' ')}
                                            size="small"
                                            color={statusColor(assignment.status)}
                                          />
                                        </TableCell>
                                        <TableCell>{formatDate(assignment.assignedDate || assignment.createdAt)}</TableCell>
                                        <TableCell>{formatDate(assignment.dueDate)}</TableCell>
                                        <TableCell align="center">
                                          <Tooltip title={removable ? 'Remove employee' : 'Cannot remove — training already started'}>
                                            <span>
                                              <IconButton size="small" color="error" disabled={!removable || submitting} onClick={() => removeAssignment(assignment)}>
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {tab === 1 && (
        <Card>
          <CardContent>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Learning Path</InputLabel>
              <Select value={selectedPathId} label="Select Learning Path" onChange={(e) => setSelectedPathId(e.target.value)}>
                {activePaths.map((path) => (
                  <MenuItem key={path.id} value={path.id}>
                    {path.title} · v{path.version || 1} · {path.isSequential === false ? 'Flexible' : 'Sequential'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedPath && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6">{selectedPath.title}</Typography>
                {selectedPath.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{selectedPath.description}</Typography>}
                <Typography variant="body2" sx={{ mt: 1 }}><strong>Total Courses:</strong> {selectedPath.courses?.length || 0}</Typography>
                <Typography variant="body2"><strong>Type:</strong> {selectedPath.isSequential === false ? 'Flexible (any order)' : 'Sequential (in order)'}</Typography>
                {(selectedPath.courses || []).map((item: any, index: number) => (
                  <Typography key={item.id || index} variant="body2">{index + 1}. {item.course?.title || 'Course'}</Typography>
                ))}
              </Paper>
            )}
            <Typography variant="h6" sx={{ mb: 1 }}>Select Employees</Typography>
            <Paper sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
              <List>
                {employees.map((employee) => (
                  <ListItem key={employee.id}>
                    <Checkbox
                      checked={selectedEmployeeIds.includes(employee.id)}
                      onChange={() => setSelectedEmployeeIds((ids) => ids.includes(employee.id) ? ids.filter((id) => id !== employee.id) : [...ids, employee.id])}
                    />
                    <ListItemText primary={employee.name} secondary={`${employee.email || ''}${employee.department ? ` • ${employee.department}` : ''}`} />
                  </ListItem>
                ))}
              </List>
            </Paper>
            {selectedEmployeeIds.length > 0 && (
              <Typography variant="body2" color="primary" sx={{ mb: 2 }}>{selectedEmployeeIds.length} employee(s) selected</Typography>
            )}
            <TextField fullWidth type="date" label="Due Date (Optional)" value={dueDate} onChange={(e) => setDueDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" disabled={submitting} onClick={() => { setSelectedPathId(''); setSelectedEmployeeIds([]); setDueDate(''); }}>Clear</Button>
              <Button variant="contained" disabled={submitting || !selectedPathId || selectedEmployeeIds.length === 0} onClick={() => assignEmployees(selectedPathId, selectedEmployeeIds, dueDate)}>
                {submitting ? 'Assigning...' : 'Assign Learning Path'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!editGroup} onClose={() => setEditGroup(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Employees to: {editGroup?.path?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Currently assigned: {editGroup?.assignments?.length || 0} employee(s)
          </Typography>
          <Paper sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
            <List>
              {availableForEdit.length === 0 ? (
                <ListItem><ListItemText primary="All employees are already assigned to this learning path" /></ListItem>
              ) : availableForEdit.map((employee) => (
                <ListItem key={employee.id}>
                  <Checkbox
                    checked={editEmployeeIds.includes(employee.id)}
                    onChange={() => setEditEmployeeIds((ids) => ids.includes(employee.id) ? ids.filter((id) => id !== employee.id) : [...ids, employee.id])}
                  />
                  <ListItemText primary={employee.name} secondary={employee.email} />
                </ListItem>
              ))}
            </List>
          </Paper>
          <TextField fullWidth type="date" label="Due Date (Optional)" value={editDueDate} onChange={(e) => setEditDueDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditGroup(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<AddIcon />} disabled={submitting || editEmployeeIds.length === 0} onClick={() => assignEmployees(editGroup.path.id, editEmployeeIds, editDueDate)}>
            {submitting ? 'Adding...' : 'Add Employees'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignLearningPathPage;
